import React, { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { gql, useApolloClient, useMutation, useQuery } from "@apollo/client"

import { Formik, Form, FieldArray, ErrorMessage } from "formik"

import { Avatar, Fieldset } from "components"

import * as yup from "yup"

import cn from "classnames"
// import formatDate from "utils/formatDate"
import formatMoney from "utils/formatMoney"
import groupBy from "lodash/groupBy"
import sortBy from "lodash/sortBy"

import formatDate from "date-fns/format"

const CREATE_USER = gql`
  mutation CreateUser($input: CreateUserInput!) {
    createUser(input: $input) {
      id
      displayName
      email
      phoneNumber
      role
    }
  }
`

const TRIP = gql`
  query Trip($id: ID!) {
    trip(id: $id) {
      id
      payment {
        price
        deposits {
          amount
          dueDate
        }
      }

      products {
        id
        name
        description
        category
        price
        availability
        quantity
      }
    }
  }
`

const CreateBooking = ({
  cart = [],
  type,
  tripId,
  userId,
  onClose,
  ...props
}) => {
  const { id } = useParams()

  const params = { variables: { id } }
  const { data, loading, error, refetch } = useQuery(TRIP, params)
  const trip = (data && data.trip) || {}

  const [createUser] = useMutation(CREATE_USER)
  const [state, transition] = useState("LOADING")
  const isEditing = cart?.length > 0

  const initialValues = {
    _type: type,
    cart,
    ...(type === "INDIVIDUAL"
      ? {
          progress: [
            { amount: null, dueDate: formatDate(new Date(), "yyyy-MM-dd") },
          ],
        }
      : {}),

    tripId,
    userId,
  }

  const bookingSchema = yup.object().shape({
    tripId: yup.string().required("Trip ID is required"),
    user: yup.mixed().when("userId", (_, schema, { parent }) => {
      if (!parent.userId) {
        return yup
          .object({
            // value: yup
            //   .object()
            //   .shape({
            //     id: yup.string().required("Lead passenger is required"),
            //   })
            //   .required("Lead passenger is required"),
          })
          .required("Lead passenger is required")
      }
    }),
    cart: yup
      .array()
      .required("You must have at least one product")
      .min(1, "You must have at least one product")
      .of(
        yup.object({
          id: yup.string().required("Product is required"),
        }),
      ),
  })

  const onSubmit = async ({ user, ...values }, actions) => {
    try {
      //This is expensive because everytime this functions gets called update will happen
      //TODO detect if values chaged, and then update shallowDeep(inital, current)

      actions.setSubmitting(true)

      let userId = values.userId
        ? values.userId
        : user?.value
        ? user?.value?.id
        : null

      if (!userId && !isEditing) {
        const {
          data: {
            createUser: { id },
          },
        } = await createUser({
          variables: {
            input: {
              displayName: user?.value
                ? user?.value?.displayName
                : user?.displayName,
              email: user?.value ? user?.value?.email : user?.email,
              role: "USER",
            },
          },
        })

        userId = id
      }

      const input = {
        ...values,
        cart: values.cart.map((product) => ({
          id: product.id,
          quantity: product?.quantity ? product?.quantity : 1,
        })),
        tripId,
        userId,
      }

      await props.onSubmit(input)

      onClose()
    } catch (e) {
      actions.setSubmitting(false)
      const errors = e.graphQLErrors.map((_) => _)
      actions.setFieldError("server", errors)
    }
  }

  const formikProps = {
    initialValues,
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema: bookingSchema,
    onSubmit,
  }

  return (
    <Formik {...formikProps}>
      {({ values, isSubmitting, setFieldValue }) => (
        <Form>
          {state === "LOADING" && <div>Loading</div>}

          {state === "NO_PRODUCTS" && (
            <div role="alert">
              <div className="bg-red-500 text-white font-bold rounded-t px-4 py-2">
                No products
              </div>
              <div className="border border-t-0 border-red-400 rounded-b bg-red-100 px-4 py-3 text-red-700">
                <p>
                  There are no products, please add products before createing
                  booking
                </p>
              </div>
            </div>
          )}

          {!userId && <UserSelection state={state} />}

          <ProductSelection
            state={state}
            transition={transition}
            values={values}
            products={trip?.products}
            onChange={(value) => setFieldValue("cart", value)}
          />

          {type === "INDIVIDUAL" && (
            <div className="px-2 py-3 mt-8">
              <h2 className="text-xl py-6">Payment schedule:</h2>

              <FieldArray
                name="progress"
                render={(arrayHelpers) => (
                  <>
                    <header className="mt-2 flex justify-between items-center px-6 py-3 border-b border-gray-200 bg-gray-100 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                      <div className="w-3/12">
                        <p>#</p>
                      </div>

                      <div className="w-3/12 ">
                        <p>Amount</p>
                      </div>

                      <div className="w-4/12">
                        <p>Due date</p>
                      </div>

                      <div className="w-1/12"></div>
                    </header>

                    <FieldDeposit
                      data={values?.progress}
                      arrayHelpers={arrayHelpers}
                    />

                    <button
                      type="button"
                      onClick={() =>
                        arrayHelpers.push({
                          amount: 0,
                          dueDate: formatDate(new Date(), "yyyy-MM-dd"),
                        })
                      }
                      className="text-blue-500 hover:text-blue-700 py-2 px-1 my-6 rounded flex"
                    >
                      <svg
                        xmlns="http:www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>

                      <span className="ml-2">Add</span>
                    </button>
                  </>
                )}
              />

              <ErrorMessage name="payment.progress">
                {(msg) =>
                  typeof msg === "string" && (
                    <div className="text-sm error bg-red-200 text-red-800 p-2 mt-2">
                      {msg}
                    </div>
                  )
                }
              </ErrorMessage>
            </div>
          )}

          <BookingSummary {...values} basePrice={trip?.payment?.price || 0} />

          {state === "READY" && (
            <button
              disabled={isSubmitting}
              type="submit"
              className="btn-submit mt-16"
            >
              {type === "INDIVIDUAL"
                ? "Create Client"
                : isEditing
                ? "Update Booking"
                : "Create Booking"}
            </button>
          )}
        </Form>
      )}
    </Formik>
  )
}

export default CreateBooking

// COMPONENTS
const UserSelection = ({ state }) => {
  const client = useApolloClient()

  const loadOptions = async (inputValue) => {
    const USERS = gql`
      query Users(
        $filter: UserFilterInput
        $page: Int
        $size: Int
        $sort: UserSortInput
      ) {
        users(filter: $filter, page: $page, size: $size, sort: $sort) {
          id
          displayName
          email
          photoURL
        }
      }
    `

    try {
      const search = inputValue?.toLowerCase()
      const { data } = await client.query({
        variables: {
          page: 0,
          size: 50,
          sort: { displayName: "ASC" },
          filter: { search },
        },
        query: USERS,
      })

      return data?.users?.map((user) => ({
        label: user.displayName,
        value: { ...user },
      }))
    } catch (error) {}
  }

  const User = ({ innerProps, ...props }) => {
    if (!props.data.value) return null

    return (
      <div {...innerProps} className="cursor-pointer">
        <div className="bg-white px-2 py-2 flex items-center hover:bg-blue-100">
          <div className="h-8 w-8 mr-4">
            <Avatar
              name={props.data.value.displayName}
              src={props.data.value.photoURL}
              size="32"
              round={true}
            />
          </div>
          <h2 className="text-base">
            {props.data.value.displayName} ({props.data.value.email})
          </h2>
        </div>
      </div>
    )
  }

  if (state !== "READY") return null

  return (
    <>
      <h2 className="text-xl py-6">Select Traveler</h2>

      <div className="px-2 py-3 mt-2">
        <Fieldset
          async
          name="user"
          component="select"
          label="Lead passenger"
          defaultOptions={loadOptions}
          loadOptions={loadOptions}
          components={{ SingleValue: User, Option: User }}
          placeholder="Select lead passenger"
        />
      </div>

      <div className="my-10 flex items-center space-x-4 w-full">
        <p>Or</p>
        <div className="w-full border-t-2 border-gray-200" />
      </div>

      <div className="py-3 flex justify-between">
        <div className="px-2 w-6/12">
          <Fieldset
            name="user.displayName"
            label="Client full name *"
            type="text"
          />
        </div>

        <div className="px-2 w-6/12">
          <Fieldset name="user.email" label="Client email *" type="email" />
        </div>
      </div>
    </>
  )
}

const ProductSelection = ({
  products,
  values,
  state,
  transition,
  onChange,
}) => {
  useEffect(() => {
    if (products?.length === 0) {
      transition("NO_PRODUCTS")
    } else {
      transition("READY")
    }
    // transition
  }, [products])

  const productsByCategory = groupBy(
    sortBy(
      products?.map((_) => ({ ..._, quantity: 1 })),
      (_) => _.price,
    ),
    (_) => _.category,
  )

  const productsModel = {
    name: "",
    description: "",
    category: "Lodging",
    quantity: "",
    price: "",
  }

  if (state !== "READY") return null

  const getIndexForId = (id) => values?.cart.findIndex((_) => _.id === id)
  const isSelected = (id) => values?.cart.map((_) => _.id).includes(id)
  const isCartEmpty = values?.cart?.length === 0

  const selectAllProducts = () => {
    onChange(
      products
        ?.filter((_) => _.availability > 0)
        .map((_) => ({
          ..._,
          quantity: _.availability > 0 ? _.availability : 1,
        })),
    )
  }

  const deselectAllProducts = () => {
    onChange([])
  }

  return (
    <div className="mt-16">
      <h2 className="text-xl py-6">Select Products</h2>

      {isCartEmpty ? (
        <button
          type="button"
          onClick={selectAllProducts}
          className="text-blue-500 hover:text-blue-700 py-2 "
        >
          Select All Products
        </button>
      ) : (
        <button
          type="button"
          onClick={deselectAllProducts}
          className="text-blue-500 hover:text-blue-700 py-2 "
        >
          Deselect All Products
        </button>
      )}

      {Object.keys(productsByCategory).map((category, index) => (
        <div className="mt-2" key={index}>
          <p className="bg-purple-100 text-purple-800 my-2 p-2 inline-block text-xs">
            {category}
          </p>

          <FieldArray
            name="cart"
            render={(arrayHelpers) => (
              <div className="px-2 py-3">
                {productsByCategory[category].map((product, index) => (
                  <Product
                    key={index}
                    index={getIndexForId(product.id)}
                    {...product}
                    isSelected={isSelected(product.id)}
                    onSelect={() => arrayHelpers.push(product)}
                    onRemove={() =>
                      arrayHelpers.remove(getIndexForId(product.id))
                    }
                  />
                ))}
              </div>
            )}
          />
        </div>
      ))}

      <ErrorMessage name="cart">
        {(msg) =>
          typeof msg === "string" && (
            <div className="text-sm error bg-red-200 text-red-800 p-2 mt-2">
              {msg}
            </div>
          )
        }
      </ErrorMessage>
    </div>
  )
}

const Product = ({
  id,
  index,
  name,
  description,
  price,
  isSelected,
  availability,
  onSelect,
  onRemove,
}) => {
  const isDisabled = availability <= 0

  const handleChange = () => {
    isSelected ? onRemove() : onSelect()
  }

  return (
    <div className="py-2 cursor-pointer">
      <label
        className={` flex items-center space-between ${
          isDisabled && "opacity-25"
        }`}
      >
        <input
          type="checkbox"
          name="cart"
          value={id}
          checked={isSelected}
          disabled={isDisabled}
          onChange={handleChange}
        />

        <div className=" mx-2 w-6/12">
          <p className="text-base leading-5 font-medium text-gray-900">
            {name}
          </p>

          <p className="text-sm leading-5 text-gray-500">{description}</p>
        </div>

        <div className="w-2/12 text-center">
          {isSelected && (
            <Fieldset
              name={`cart[${index}].quantity`}
              // label="quantity *"
              // type="number"
              component="count"
              max={availability}
              // placeholder="Enter trip code"
            />
          )}
        </div>

        <div className="flex-1">
          <span
            className={cn(
              `px-2 inline-flex text-xs leading-5 font-semibold rounded-full uppercase`,
              {
                [`bg-green-100 text-green-800`]:
                  availability >= 3 || availability === null,
                [`bg-yellow-100 text-yellow-800`]: availability < 3,
              },
            )}
          >
            {availability === null ? "∞" : availability < 0 ? 0 : availability}{" "}
            on stock
          </span>
        </div>

        <p>{price === 0 ? "Incl." : formatMoney(price)}</p>
      </label>
    </div>
  )
}

// PaymentSelection
const FieldDeposit = ({ getIndex, data, endDateMax, arrayHelpers }) => {
  return (
    <div className="flex flex-col">
      {data?.map((part, index) => (
        <>
          <div
            key={index}
            className={`flex justify-between items-center my-4 px-6 space-x-2`}
          >
            <div className="w-3/12 text-base capitalize">
              <label>
                {index + 1}
                {/* {data.length === 1 || index !== data.length - 1
                  ? `${stringifyNumber(index + 1)} deposit`
                  : "Final payment"} */}
              </label>
            </div>

            <div className="w-3/12">
              <Fieldset name={`progress[${index}].amount`} type="number" />
            </div>

            <div className="w-4/12 flex justify-between items-center">
              <Fieldset
                name={`progress[${index}].dueDate`}
                type="date"
                // min={TODAY}
                // max={endDateMax}
              />
            </div>

            <div className="w-1/12 flex justify-end items-center">
              <button
                type="button"
                className="inline-block"
                onClick={() => arrayHelpers.remove(index)}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9 2C8.62123 2 8.27497 2.214 8.10557 2.55279L7.38197 4H4C3.44772 4 3 4.44772 3 5C3 5.55228 3.44772 6 4 6L4 16C4 17.1046 4.89543 18 6 18H14C15.1046 18 16 17.1046 16 16V6C16.5523 6 17 5.55228 17 5C17 4.44772 16.5523 4 16 4H12.618L11.8944 2.55279C11.725 2.214 11.3788 2 11 2H9ZM7 8C7 7.44772 7.44772 7 8 7C8.55228 7 9 7.44772 9 8V14C9 14.5523 8.55228 15 8 15C7.44772 15 7 14.5523 7 14V8ZM12 7C11.4477 7 11 7.44772 11 8V14C11 14.5523 11.4477 15 12 15C12.5523 15 13 14.5523 13 14V8C13 7.44772 12.5523 7 12 7Z"
                    fill="#4A5568"
                  />
                </svg>
              </button>
            </div>
          </div>
        </>
      ))}
    </div>
  )
}

const mockBooking = {
  type: "group",
  user: {
    label: "Albert Heckers",
    value: {
      id: "2fed1984-f160-46e8-aae1-2b311a84cf9c",
      displayName: "Albert Heckers",
      email: "cjanks222@comcast.net",
      photoURL: null,
    },
  },
  trip: {
    label: "Athens Mykonos Santorini",
    value: {
      id: "2b6c6e73-52b4-402a-b531-51bf7390e342",
      name: "Athens Mykonos Santorini",
      url: "https://grouptravelres.com/trips/athens-mykonos-santorini-8715",
      code: "8715",
      startDate: "2020-07-14T00:00:00.000Z",
      endDate: "2020-07-23T00:00:00.000Z",
      photoURL:
        "https://storage.googleapis.com/grouptravelres.appspot.com/Oia%2C_Santorini_HDR_sunset.jpeg?GoogleAccessId=firebase-adminsdk-bkb5a%40grouptravelres.iam.gserviceaccount.com&Expires=4102441200&Signature=a5JmaRy93%2BUBKjzN0lR%2BuYLda9%2FWnvlHIoPGY%2FONby0MaTwDIcRaCBHMWrj5zm%2FyfAHDbz%2BU5lIMVesQeSzO1CaceDKbovm1aHLi9YybyAxph6xUht%2BhZ8XwZSKEEojnxHsWl%2BvnEyLfoAv4jpX9UViVv6U94V4izvQpvMZNs07B5PXFTLS8Vdp7CJUJjfMj5wJVsBiSm7X4t3PIFj1PYoOkohIxA31C%2B7FaCdXkvulGIVZQFPc4ZlYZgCltKm%2FjIAq8vQoqqNolEjYNC5pyB9cmOvlyRRIfZEoLxaTelWdfvHwOq1GC4ByEIK2H6pjh4oRFTj%2B6378K6EmQbLpl%2FA%3D%3D",
      payment: { price: 2750 },
      products: [
        {
          id: "fa90ded1-921e-4e6c-8520-e2900ff1249e",
          name: "DOUBLE - Twin Room ",
          description: null,
          category: "Lodging",
          price: 0,
          availability: 3,
          quantity: 13,
        },
        {
          id: "909a9cec-f3f3-4819-8571-4cb071e23082",
          name: "SINGLE - One Double Bed",
          description: null,
          category: "Lodging",
          price: 1700,
          availability: 0,
          quantity: 1,
        },
        {
          id: "1b1b132d-87c6-415c-94c5-c77b2fdb2f8d",
          name: "DOUBLE - Twin Room Seaview Jr. S",
          description: null,
          category: "Lodging",
          price: 179,
          availability: 0,
          quantity: 5,
        },
        {
          id: "aa03a8ed-ecb4-45c5-ab6a-4633afc7a432",
          name: "SINGLE -  Seaview Jr. Suite",
          description: null,
          category: "Lodging",
          price: 2055,
          availability: 0,
          quantity: 1,
        },
        {
          id: "76c427ba-46a2-41ed-aa05-6c3c3d55f451",
          name: "DOUBLE - Deluxe Room with pool",
          description: null,
          category: "Lodging",
          price: 370,
          availability: 2,
          quantity: 6,
        },
        {
          id: "1e7a6381-da09-4b31-9aeb-7d9211a7f67b",
          name: "DOUBLE - Twin Room MALE",
          description: null,
          category: "Lodging",
          price: 0,
          availability: 1,
          quantity: 1,
        },
        {
          id: "66b701f2-211e-4e3a-8622-87ad14364b74",
          name: "DBL FEMALE JR STE Sea View",
          description: null,
          category: "Lodging",
          price: 179,
          availability: 0,
          quantity: 1,
        },
        {
          id: "ed62839f-29bf-4612-ae7e-53b60e5aa73d",
          name: "Guided walking tour of Mykonos",
          description: null,
          category: "Optionals",
          price: 50,
          availability: null,
          quantity: null,
        },
        {
          id: "74b6de77-931c-4510-a075-1d655049fb80",
          name: "Cooking Class  with the Locals",
          description: null,
          category: "Optionals",
          price: 130,
          availability: null,
          quantity: null,
        },
        {
          id: "d5c65a29-a6be-4809-834d-c117059ca1df",
          name: "Group Flights from Newark ",
          description: null,
          category: "Air",
          price: 1100,
          availability: 17,
          quantity: 25,
        },
      ],
    },
  },
  cart: [],
}

const BookingSummary = ({ user, cart, basePrice }) => {
  const cartByCategory = groupBy(cart, (_) => _.category)

  const total = cart?.reduce(
    (a, b) =>
      a +
      (b.category === "Lodging" ? b.price + basePrice : b.price) * b.quantity,
    0,
  )
  const numberOfLodging = cart
    ?.filter((_) => _.category === "Lodging")
    ?.reduce((a, b) => a + b.quantity, 0)

  return (
    <div className="mt-16">
      <h2 className="text-xl py-6">Booking Summary</h2>

      {numberOfLodging > 0 && (
        <div className="flex justify-between items-center">
          <p variant="small">Number of travelers:</p>
          <p variant="small">{numberOfLodging}</p>
        </div>
      )}

      {basePrice > 0 && (
        <div className="flex justify-between items-center">
          <p variant="small">Base price:</p>
          <p variant="small">
            {numberOfLodging > 0 && `${numberOfLodging} x `}{" "}
            {formatMoney(basePrice)}
          </p>
        </div>
      )}

      {total && (
        <div className="flex justify-between items-center">
          <p variant="small">Total:</p>
          <p variant="small">
            {total === 0 ? formatMoney(basePrice) : formatMoney(total)}
          </p>
        </div>
      )}
    </div>
  )
}
